'use client';

import { MIND_MODE, WS_MIND_TYPE } from '@mindverse/accessor-open';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { forwardRef, memo, useCallback, useContext, useEffect, useRef } from 'react';

import App, { EAppMode } from '@/components/chat-app';
import HomeContext from '@/context/HomeContext';
import { useChatManager } from '@/hooks/useChatManager';
import useGenius from '@/hooks/useGenius';
import useGeniusLayout from '@/hooks/useGeniusLayout';
import { useModel } from '@/hooks/useModel';
import { EVENT } from '@/utils/event';

import CircleChat from '../CircleChat';
import HomePage from '../HomePage';
import TopBar from './TopBar';
// import Tutorial from '../tutorial';
import styles from './index.less';

interface IProps {
  inMobileDevice?: boolean;
  className?: string;
}

// eslint-disable-next-line react/display-name
const PersonalAIPage = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const { inMobileDevice, className } = props;
  const detailInfo = useGenius();

  const { voiceMute, chatHomepageRoute, chatCircleRoute } = useContext(HomeContext);
  const { shadowMindId: mindId, mindName } = detailInfo;
  const sessionRef = useRef<string>('');
  const { sessionSocketManager } = useChatManager();
  const { chatStatus } = useGeniusLayout();
  const { userProfile } = useModel('auth-model', (model) => ({ userProfile: model.userProfile }));

  const searchParams = useSearchParams();
  const searchSessionId = searchParams.get('sessionId');

  useEffect(() => {
    if (searchSessionId) {
      const newSearchParams = new URLSearchParams(searchParams);

      dispatchEvent(new CustomEvent(EVENT.CHANGE_CHAT_STATUS_LAYOUT_TRULY, { detail: 'normal' }));

      newSearchParams.delete('sessionId');
      const newUrl = `${window.location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams}` : ''
      }`;

      history.replaceState(null, '', newUrl);
    }
  }, [searchParams, searchSessionId]);

  const sessionCb = useCallback((_mindId: string, sessionId: string) => {
    sessionRef.current = sessionId;
    dispatchEvent(new CustomEvent(EVENT.SET_CURRENT_SESSION_ID, { detail: sessionId }));
  }, []);

  useEffect(() => {
    function getSessionId(event: Event): void {
      const callBack = (event as CustomEvent).detail;

      if (mindId && callBack) {
        callBack(sessionRef.current);
      }
    }

    window.addEventListener(EVENT.GET_SESSION_ID, getSessionId);

    return () => {
      window.removeEventListener(EVENT.GET_SESSION_ID, getSessionId);
    };
  }, [mindId]);

  if (!mindId) {
    return <div ref={ref} />;
  }

  return (
    <div
      ref={ref}
      className={classNames(styles.personalAIPage, className, {
        [styles.friendHomepage]: !!chatHomepageRoute,
        [styles.circleChat]: !!chatCircleRoute,
        [styles.inMobileDevice]: inMobileDevice
      })}
    >
      {inMobileDevice || chatHomepageRoute || chatCircleRoute ? null : (
        <TopBar className="!z-10" mindId={mindId} mindName={mindName} />
      )}
      {chatHomepageRoute ? (
        <HomePage
          key={chatHomepageRoute}
          className={!inMobileDevice ? 'rounded-[28px]' : ''}
          forceInMobile={inMobileDevice || chatStatus !== 'fullScreen'}
          origin="chat"
          route={chatHomepageRoute}
        />
      ) : null}
      {chatCircleRoute ? (
        <CircleChat
          key={chatCircleRoute}
          className={!inMobileDevice ? 'rounded-[28px]' : ''}
          forceInMobile={inMobileDevice || chatStatus !== 'fullScreen'}
          route={chatCircleRoute}
        />
      ) : null}
      {/* 只有在没有打开 homepage 或 circle 时才渲染 chatApp,避免多个 App 实例同时监听事件 */}
      {!chatHomepageRoute && !chatCircleRoute ? (
        <div className={styles.chatApp}>
          <App
            chatContainer={{
              disabled: false,
              appMode:
                inMobileDevice || chatStatus !== 'fullScreen'
                  ? EAppMode.CHAT_MOBILE
                  : EAppMode.CHAT_MODE,
              config: {
                mindConfig: {
                  mindId: mindId,
                  mindType: WS_MIND_TYPE.shadow,
                  mode: MIND_MODE.PERSONAL // pa的mode需要是personal
                },
                userId: userProfile?.userId,
                sessionManager: sessionSocketManager,
                voiceMute: voiceMute,
                originSessionId: searchSessionId || undefined
              },
              sessionCb
            }}
          />
        </div>
      ) : null}
    </div>
  );
});

export default memo(PersonalAIPage);
